import { useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { useApiConfig, type MutationApiConfig } from "@visioni/a3s-client-api/app";
import { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import { createConfirmedDumpMutation } from "./queries";

/**
 * Records a confirmed dump against a specific camera.
 *
 * `siteId`/`cameraKey` come from the route as raw strings; they're parsed to
 * branded ids here so callers (the result screen) don't have to. `tenantId`
 * and `apiBaseUrl` are pulled from {@link useApiConfig} — the mutation factory
 * throws at call time (not mount time) if either is missing, so the hook is
 * safe to mount before the tenant context has resolved.
 *
 * Lives in the a3s-dumping feature package (not the app) so the whole
 * confirm-dump flow — endpoint, mutation, and its React binding — ships as one
 * unit, mirroring `useOpenGate` in a3s-gate-control.
 */
export function useConfirmDump(params: { siteId: string; cameraKey: string }) {
  const apiConfig = useApiConfig();

  const mutationConfig: MutationApiConfig = useMemo(
    () => ({
      tenantId: apiConfig.tenant?.tenantId,
      apiBaseUrl: apiConfig.tenant?.apiBaseUrl,
      authenticatedFetch: apiConfig.authenticatedFetch,
    }),
    [apiConfig.tenant, apiConfig.authenticatedFetch],
  );

  const pathParams = useMemo(
    () => ({
      siteId: SiteId.fromString(params.siteId),
      assetCameraKey: AssetCameraKey.fromString(params.cameraKey),
    }),
    [params.siteId, params.cameraKey],
  );

  const mutationOptions = useMemo(
    () => createConfirmedDumpMutation(mutationConfig, pathParams),
    [mutationConfig, pathParams],
  );

  return useMutation({
    ...mutationOptions,
    retry: false,
  });
}
